"use client";

import type { ReactNode } from "react";
import {
  reportPlanSummaryMap as SEO_REPORT_CTA_PLANS,
  type ReportPlanSummary as SEOReportCTAPlan,
} from "@ishvaram/domain-data/report-plan-summaries";
import OfferImpression from "@/components/OfferImpression";
import { trackReportInputCollectionStarted } from "@/lib/analytics-agent-offers";
import { offerPresentationId } from "@/lib/analytics-offer-milestones";
import { safeSessionSet } from "@/lib/browser-storage";
import {
  ISHVARAM_COMMERCE_SYSTEM,
  ISHVARAM_REPORT_CHECKOUT_CHANNEL,
} from "@/lib/commerce-attribution";
import {
  trackSEOAgentBridgeClickedLazy,
  trackSEOIntentCapturedLazy,
} from "@/lib/lazy-analytics-seo-bridge";
import { scorePurchaseIntent } from "@/lib/purchase-intent-score";
import {
  getSEOIntentConfig,
  getSEOIntentConfigForSource,
  type SEOIntent,
  type SEOReportPlanId,
} from "@/lib/seo-intents";

const REPORT_PROMPT = {
  brihat: "Prepare my Brihat Kundali written report after reading my chart.",
  marriage: "Read my chart for marriage problems and prepare my Marriage report.",
  career: "Read my 10th house and prepare my Career & Finance report.",
} as const;

type SEOReportCTAConfig = {
  sourcePath: string;
  placement: string;
  reportPrompt: string;
  fallbackIntent: string;
  planId: SEOReportPlanId;
  configIntent?: SEOIntent;
  heading: string;
  body: (plan: SEOReportCTAPlan) => ReactNode;
  buttonLabel: string;
};

const SEO_REPORT_CTA_CONFIG = {
  "free-astrology-chat": {
    // Only consumer is /hi/muft-jyotish-chat/ since the en page 301-consolidated
    // into /ai-astrologer/ (2026-07-08) — source_path reflects the live surface.
    sourcePath: "/hi/muft-jyotish-chat",
    placement: "free_astrology_chat_written_report_cta",
    reportPrompt: REPORT_PROMPT.brihat,
    fallbackIntent: "chart_report",
    planId: "brihat_kundali",
    configIntent: "chart_report",
    heading: "Start with free chat, then keep the full chart",
    body: (plan) => (
      <>
        The free chat answers the question on your mind first. If you want the complete written
        chart after that, unlock the {plan.pages}-page Brihat Kundali with doshas, dasha periods,
        planetary positions, and exact upays for Rs {plan.price_inr.toLocaleString("en-IN")}.
      </>
    ),
    buttonLabel: "Start free chat",
  },
  "chat-with-astrologer": {
    sourcePath: "/chat-with-astrologer",
    placement: "chat_with_astrologer_written_report_cta",
    reportPrompt: REPORT_PROMPT.brihat,
    fallbackIntent: "chart_report",
    planId: "brihat_kundali",
    configIntent: "chart_report",
    heading: "Chat free first, then keep the full chart in writing",
    body: (plan) => (
      <>
        The free chat answers your question first. If you want your complete kundali in writing
        after that, unlock the {plan.pages}-page Brihat Kundali report with doshas, dasha periods,
        planetary positions, and exact upays for Rs {plan.price_inr.toLocaleString("en-IN")}.
      </>
    ),
    buttonLabel: "Chat with the AI Jyotishi free",
  },
  "talk-to-astrologer": {
    sourcePath: "/talk-to-astrologer",
    placement: "talk_to_astrologer_written_report_cta",
    reportPrompt: REPORT_PROMPT.brihat,
    fallbackIntent: "chart_report",
    planId: "brihat_kundali",
    configIntent: "chart_report",
    heading: "Talk free first, then keep the full chart in writing",
    body: (plan) => (
      <>
        The free chat answers what is on your mind right now. If you want your complete kundali in
        writing after that, unlock the {plan.pages}-page Brihat Kundali report with doshas, dasha
        periods, planetary positions, and exact upays for Rs{" "}
        {plan.price_inr.toLocaleString("en-IN")}.
      </>
    ),
    buttonLabel: "Talk to the AI Jyotishi free",
  },
  "astrologer-for-marriage-problem": {
    sourcePath: "/astrologer-for-marriage-problem",
    placement: "marriage_problem_report_cta",
    reportPrompt: REPORT_PROMPT.marriage,
    fallbackIntent: "matching",
    planId: "marriage_report",
    heading: "Talk free first, then read the full marriage analysis",
    body: (plan) => (
      <>
        The free chat names the planet behind the marriage problem. If you want both charts read in
        writing after that, unlock the {plan.pages}-page Marriage &amp; Compatibility report with
        Gun Milan, Mangal Dosh, D9/Navamsha, dasha timing, and upay for Rs{" "}
        {plan.price_inr.toLocaleString("en-IN")}.
      </>
    ),
    buttonLabel: "Ask about the marriage problem free",
  },
  "career-astrology-consultation": {
    sourcePath: "/career-astrology-consultation",
    placement: "career_consultation_report_cta",
    reportPrompt: REPORT_PROMPT.career,
    fallbackIntent: "career",
    planId: "career_report",
    heading: "Talk free first, then read the full career analysis",
    body: (plan) => (
      <>
        The free chat names the planet blocking your career. If you want the full read in writing
        after that, unlock the {plan.pages}-page Career &amp; Finance report with your 10th house,
        D10/Dasamsa, dasha timeline, transits, wealth yogas, and upay for Rs{" "}
        {plan.price_inr.toLocaleString("en-IN")}.
      </>
    ),
    buttonLabel: "Ask about your career free",
  },
} satisfies Record<string, SEOReportCTAConfig>;

export type SEOReportCTAVariant = keyof typeof SEO_REPORT_CTA_CONFIG;

function fallbackHref(config: SEOReportCTAConfig) {
  const params = new URLSearchParams({
    intent: config.fallbackIntent,
    source: config.sourcePath,
    prompt: config.reportPrompt,
    auto: "1",
  });
  return `/free-kundali/#${params.toString()}`;
}

function resolveIntentConfig(config: SEOReportCTAConfig) {
  return config.configIntent
    ? getSEOIntentConfig(config.configIntent)
    : getSEOIntentConfigForSource(config.sourcePath);
}

export default function SEOReportCTA({ variant }: { variant: SEOReportCTAVariant }) {
  const cta = SEO_REPORT_CTA_CONFIG[variant];
  const plan = SEO_REPORT_CTA_PLANS[cta.planId];
  const intentConfig = resolveIntentConfig(cta);
  const presentationId = offerPresentationId({
    actionMode: "input_collection",
    planId: cta.planId,
    placement: cta.placement,
    sourcePath: cta.sourcePath,
  });

  function startReportFlow() {
    const purchaseIntent = scorePurchaseIntent({
      intent: intentConfig.intent,
      sourcePath: cta.sourcePath,
      primaryOffer: intentConfig.primaryOffer,
    });

    try {
      safeSessionSet("seo_entry_intent", intentConfig.intent);
      safeSessionSet("seo_entry_path", cta.sourcePath);
      safeSessionSet("seo_entry_page_category", intentConfig.pageCategory);
      safeSessionSet("seo_entry_primary_offer", intentConfig.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", intentConfig.reportPlanId);
      safeSessionSet("seo_entry_prompt", cta.reportPrompt);
      safeSessionSet("seo_entry_auto_send", "1");
      safeSessionSet("seo_entry_purchase_intent_score", String(purchaseIntent.score));
      safeSessionSet("seo_entry_purchase_intent_band", purchaseIntent.band);
      safeSessionSet("seo_entry_purchase_intent_reason", purchaseIntent.reason);
    } catch {
      // The fallback navigation still carries the intent in the hash.
    }

    // This button scrolls or navigates to the birth-detail/chat intake; checkout
    // does not exist yet. Keep its action paired with the rendered input-collection
    // impression through one presentation identity (measurement rule 67).
    trackReportInputCollectionStarted({
      plan_id: cta.planId,
      source_path: cta.sourcePath,
      intent: intentConfig.intent,
      placement: cta.placement,
      checkout_channel: ISHVARAM_REPORT_CHECKOUT_CHANNEL,
      commerce_system: ISHVARAM_COMMERCE_SYSTEM,
      ranked_offer_order: [cta.planId],
      presentation_id: presentationId,
    });
    trackSEOAgentBridgeClickedLazy({
      source_path: cta.sourcePath,
      intent: intentConfig.intent,
      page_category: intentConfig.pageCategory,
      offer_type: intentConfig.primaryOffer,
      plan_id: intentConfig.reportPlanId,
      target: intentConfig.target,
      placement: cta.placement,
      purchase_intent_score: purchaseIntent.score,
      purchase_intent_band: purchaseIntent.band,
      purchase_intent_reason: purchaseIntent.reason,
    });
    trackSEOIntentCapturedLazy({
      source_path: cta.sourcePath,
      intent: intentConfig.intent,
      page_category: intentConfig.pageCategory,
      target: intentConfig.target,
      placement: cta.placement,
      chip_prompt: true,
    });

    const form = document.getElementById("start-reading");
    if (!form) {
      window.location.href = fallbackHref(cta);
      return;
    }

    window.dispatchEvent(new Event("seo-intent-refresh"));
    form.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <section className="my-10 rounded-2xl border border-amber-200 bg-amber-50/60 p-6 md:p-8">
      {/* This shared CTA owns the fire-once offer_seen for ALL its variants
          (rule 47) — same planId/sourcePath/placement its click tracking sends,
          so both CTR legs share one attribution surface (rule 60). Variant
          wrappers must NOT add their own <OfferImpression> (double-count). */}
      <OfferImpression
        planId={cta.planId}
        actionMode="input_collection"
        sourcePath={cta.sourcePath}
        intent={intentConfig.intent}
        placement={cta.placement}
        rankedOfferOrder={[cta.planId]}
        presentationKey={presentationId}
      />
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{cta.heading}</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-700">{cta.body(plan)}</p>
        </div>
        <button
          type="button"
          onClick={startReportFlow}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-amber-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-800"
        >
          {cta.buttonLabel}
        </button>
      </div>
    </section>
  );
}
